import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, BarChart3 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

interface DomainAnalyticsSectionProps {
  domains: any[];
  onFilterChange?: (filterType: string, filterValue: string) => void;
}

interface AnalyticsData {
  name: string;
  totalDomains: number;
  replyRate: number;
  bounceRate: number;
  totalSent: number;
  totalReplies: number;
  totalBounces: number;
}

const getBarColor = (replyRate: number) => {
  if (replyRate >= 3) return '#10b981'; // green
  if (replyRate >= 1.5) return '#f59e0b'; // yellow
  if (replyRate > 0) return '#ef4444'; // red
  return '#6b7280'; // gray
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-600">Domains: {data.totalDomains}</p>
        <p className="text-sm text-gray-600">Total Sent: {data.totalSent.toLocaleString()}</p>
        <p className="text-sm text-gray-600">Total Replies: {data.totalReplies.toLocaleString()}</p>
        <p className="text-sm text-gray-600">Total Bounces: {data.totalBounces.toLocaleString()}</p>
        <p className="text-sm text-gray-600">Reply Rate: {data.replyRate}%</p>
        <p className="text-sm text-gray-600">Bounce Rate: {data.bounceRate}%</p>
      </div>
    );
  }
  return null;
};

const AnalyticsChart = ({ data, title, onBarClick }: { data: AnalyticsData[]; title: string; onBarClick?: (name: string) => void }) => {
  if (!data || data.length === 0) {
    return (
      <div className="space-y-2">
        <h4 className="text-base font-semibold text-foreground">{title}</h4>
        <div className="flex items-center justify-center h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <div className="text-center">
            <BarChart3 className="h-8 w-8 text-gray-400 mx-auto mb-1" />
            <p className="text-gray-500 text-sm font-medium">No data available</p>
            <p className="text-gray-400 text-xs">Configure providers to see analytics</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      <h4 className="text-base font-semibold text-foreground mb-1 mt-3 pt-3 border-t border-gray-200">{title}</h4>
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 80,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              angle={-45}
              textAnchor="end"
              height={100}
              fontSize={12}
            />
            <YAxis 
              fontSize={12}
              label={{ value: 'Reply Rate (%)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="replyRate" 
              radius={[4, 4, 0, 0]}
              onClick={(data) => onBarClick && onBarClick(data.name)}
              style={{ cursor: onBarClick ? 'pointer' : 'default' }}
              name="Reply Rate"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.replyRate)} />
              ))}
              <LabelList 
                dataKey="replyRate" 
                position="center" 
                formatter={(value: number) => `${value}%`}
                style={{ 
                  fill: 'white', 
                  fontSize: '12px', 
                  fontWeight: 'bold',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.7)'
                }}
              />
            </Bar>
            <Bar dataKey="bounceRate" radius={[4, 4, 0, 0]} fill="#ef4444" fillOpacity={0.6} name="Bounce Rate">
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Data Summary Table */}
      <div className="rounded-lg p-3 border bg-muted/50 w-full">
        <div className="grid grid-cols-2 gap-3">
          {data.slice(0, 6).map((item, index) => (
            <div key={index} className="bg-card rounded-lg p-3 border hover:bg-accent/50 transition-colors cursor-pointer"
                 onClick={() => onBarClick && onBarClick(item.name)}>
              <div className="text-sm font-semibold text-foreground truncate mb-2" title={item.name}>
                {item.name}
              </div>
              
              {/* Reply Rate - Prominent */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground font-medium">Reply Rate</span>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{item.replyRate}%</span>
                </div>
              </div>
              
              {/* Bounce Rate */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground font-medium">Bounce Rate</span>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-rose-500"></div>
                  <span className="text-sm font-bold text-rose-600 dark:text-rose-400">{item.bounceRate}%</span>
                </div>
              </div>
              
              {/* Domain Count */}
              <div className="text-xs text-muted-foreground text-center bg-muted rounded px-2 py-1">
                {item.totalDomains} domain{item.totalDomains !== 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
        {data.length > 6 && (
          <div className="text-center mt-3">
            <span className="text-xs text-muted-foreground bg-card px-2 py-1 rounded border">
              + {data.length - 6} more entries • Click chart bars or boxes to filter
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export function DomainAnalyticsSection({ domains, onFilterChange }: DomainAnalyticsSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Debug logging
  React.useEffect(() => {
    console.log('DomainAnalyticsSection - domains data:', domains);
    console.log('DomainAnalyticsSection - domains length:', domains?.length || 0);
    if (domains && domains.length > 0) {
      console.log('Sample domain:', domains[0]);
    }
  }, [domains]);

  const registrarAnalytics = useMemo(() => {
    const registrarMap = new Map<string, { domains: number; replies: number; bounces: number; sent: number }>();

    domains.forEach(domain => {
      const registrar = domain.domain_registrar || 'Unknown';
      const totalReplies = domain.total_email_replies || 0;
      const totalBounces = domain.total_email_bounces || 0;
      const totalSent = domain.total_emails_sent || 0;

      if (!registrarMap.has(registrar)) {
        registrarMap.set(registrar, { domains: 0, replies: 0, bounces: 0, sent: 0 });
      }

      const current = registrarMap.get(registrar)!;
      current.domains += 1;
      current.replies += totalReplies;
      current.bounces += totalBounces;
      current.sent += totalSent;
    });

    return Array.from(registrarMap.entries()).map(([name, data]) => ({
      name,
      totalDomains: data.domains,
      totalReplies: data.replies,
      totalBounces: data.bounces,
      totalSent: data.sent,
      replyRate: data.sent > 0 ? Number(((data.replies / data.sent) * 100).toFixed(2)) : 0,
      bounceRate: data.sent > 0 ? Number(((data.bounces / data.sent) * 100).toFixed(2)) : 0,
    })).sort((a, b) => b.replyRate - a.replyRate);
  }, [domains]);

  const emailProviderAnalytics = useMemo(() => {
    const providerMap = new Map<string, { domains: number; replies: number; bounces: number; sent: number }>();

    domains.forEach(domain => {
      const provider = domain.email_provider || 'Unknown';
      const totalReplies = domain.total_email_replies || 0;
      const totalBounces = domain.total_email_bounces || 0;
      const totalSent = domain.total_emails_sent || 0;

      if (!providerMap.has(provider)) {
        providerMap.set(provider, { domains: 0, replies: 0, bounces: 0, sent: 0 });
      }

      const current = providerMap.get(provider)!;
      current.domains += 1;
      current.replies += totalReplies;
      current.bounces += totalBounces;
      current.sent += totalSent;
    });

    return Array.from(providerMap.entries()).map(([name, data]) => ({
      name,
      totalDomains: data.domains,
      totalReplies: data.replies,
      totalBounces: data.bounces,
      totalSent: data.sent,
      replyRate: data.sent > 0 ? Number(((data.replies / data.sent) * 100).toFixed(2)) : 0,
      bounceRate: data.sent > 0 ? Number(((data.bounces / data.sent) * 100).toFixed(2)) : 0,
    })).sort((a, b) => b.replyRate - a.replyRate);
  }, [domains]);

  const mailboxProviderAnalytics = useMemo(() => {
    const providerMap = new Map<string, { domains: number; replies: number; bounces: number; sent: number }>();

    domains.forEach(domain => {
      const provider = domain.mailbox_provider || 'Unknown';
      const totalReplies = domain.total_email_replies || 0;
      const totalBounces = domain.total_email_bounces || 0;
      const totalSent = domain.total_emails_sent || 0;

      if (!providerMap.has(provider)) {
        providerMap.set(provider, { domains: 0, replies: 0, bounces: 0, sent: 0 });
      }

      const current = providerMap.get(provider)!;
      current.domains += 1;
      current.replies += totalReplies;
      current.bounces += totalBounces;
      current.sent += totalSent;
    });

    return Array.from(providerMap.entries()).map(([name, data]) => ({
      name,
      totalDomains: data.domains,
      totalReplies: data.replies,
      totalBounces: data.bounces,
      totalSent: data.sent,
      replyRate: data.sent > 0 ? Number(((data.replies / data.sent) * 100).toFixed(2)) : 0,
      bounceRate: data.sent > 0 ? Number(((data.bounces / data.sent) * 100).toFixed(2)) : 0,
    })).sort((a, b) => b.replyRate - a.replyRate);
  }, [domains]);

  const purchaseDateAnalytics = useMemo(() => {
    const ageMap = new Map<string, { domains: number; replies: number; bounces: number; sent: number }>();

    domains.forEach(domain => {
      let ageGroup = 'Unknown';
      if (domain.purchase_date) {
        try {
          const purchaseDate = new Date(domain.purchase_date);
          if (!isNaN(purchaseDate.getTime())) {
            const now = new Date();
            const ageInDays = Math.floor((now.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24));
            const ageInMonths = ageInDays / 30;

            if (ageInMonths < 1) {
              ageGroup = '< 1 month';
            } else if (ageInMonths < 3) {
              ageGroup = '1-3 months';
            } else if (ageInMonths < 6) {
              ageGroup = '3-6 months';
            } else if (ageInMonths < 12) {
              ageGroup = '6-12 months';
            } else if (ageInMonths < 24) {
              ageGroup = '1-2 years';
            } else {
              ageGroup = '2+ years';
            }
          }
        } catch (e) {
          // Keep as 'Unknown' if date parsing fails
        }
      }

      const totalReplies = domain.total_email_replies || 0;
      const totalBounces = domain.total_email_bounces || 0;
      const totalSent = domain.total_emails_sent || 0;

      if (!ageMap.has(ageGroup)) {
        ageMap.set(ageGroup, { domains: 0, replies: 0, bounces: 0, sent: 0 });
      }

      const current = ageMap.get(ageGroup)!;
      current.domains += 1;
      current.replies += totalReplies;
      current.bounces += totalBounces;
      current.sent += totalSent;
    });

    // Define the sort order for age groups
    const ageOrder = ['< 1 month', '1-3 months', '3-6 months', '6-12 months', '1-2 years', '2+ years', 'Unknown'];

    return Array.from(ageMap.entries()).map(([name, data]) => ({
      name,
      totalDomains: data.domains,
      totalReplies: data.replies,
      totalBounces: data.bounces,
      totalSent: data.sent,
      replyRate: data.sent > 0 ? Number(((data.replies / data.sent) * 100).toFixed(2)) : 0,
      bounceRate: data.sent > 0 ? Number(((data.bounces / data.sent) * 100).toFixed(2)) : 0,
    })).sort((a, b) => {
      const aIndex = ageOrder.indexOf(a.name);
      const bIndex = ageOrder.indexOf(b.name);
      return aIndex - bIndex;
    });
  }, [domains]);

  return (
    <Card className="w-full">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BarChart3 className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Domain Analytics by Provider</CardTitle>
              </div>
              <Button variant="ghost" size="sm">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-2">
            <div className="text-sm text-muted-foreground">
              Analyze reply rate% and bounce rate% performance across different domain configurations and time periods.
            </div>
            
            {/* 2-Column Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <AnalyticsChart 
                data={registrarAnalytics} 
                title="Performance by Domain Registrar"
                onBarClick={(name) => onFilterChange && onFilterChange('domain_registrar', name)}
              />
              
              <AnalyticsChart 
                data={emailProviderAnalytics} 
                title="Performance by Email Provider"
                onBarClick={(name) => onFilterChange && onFilterChange('email_provider', name)}
              />

              <AnalyticsChart 
                data={mailboxProviderAnalytics} 
                title="Performance by Mailbox Provider"
                onBarClick={(name) => onFilterChange && onFilterChange('mailbox_provider', name)}
              />

              <AnalyticsChart 
                data={purchaseDateAnalytics} 
                title="Performance by Domain Age"
                onBarClick={(name) => onFilterChange && onFilterChange('purchase_date', name)}
              />
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}